/**
 * The canonical brand-mention matcher.
 *
 * Every surface that asks "is this raw mention string our brand, or one of our
 * tracked competitors?" answers it here: the stored share-of-voice analysis,
 * the headline discovery metrics, historical metrics, the report export's
 * logo grid and the web report's logo grid. They used to carry three
 * different matchers, which is how a report showed a competitor at 0% share of
 * voice in its table while its logo grid listed it in half the cells.
 *
 * The rules:
 *
 * 1. Both sides (the mention and each name/alias) are normalized the same way
 *    (`normalizeBrandMentionText`): diacritics folded, lowercase, markdown,
 *    emoji / medal symbols and list numbering ("🥈 2.", "1. 🥇") stripped,
 *    `&` and `+` read as "and" and the connector "and" then dropped (so
 *    "Ropes & Gray", "Ropes and Gray" and "Ropes Gray" are one name),
 *    apostrophes deleted ("McDonald's" == "McDonalds"), remaining punctuation
 *    turned into spaces, runs of single-letter initials joined ("P. A." →
 *    "pa"), and trailing legal suffixes (LLP, Inc, GmbH, PA, ...) dropped.
 * 2. A name matches a mention when
 *    - (strong) the normalized name equals the normalized mention, or appears
 *      inside it as a whole-word run; or
 *    - (weak) the mention, with any parenthetical and anything after a colon
 *      removed, is a LEADING run of at least two of the name's tokens
 *      ("Preti Flaherty" for "Preti, Flaherty, Beliveau & Pachios LLP").
 *    Never the reverse otherwise, and a single token never prefix-matches: a
 *    bare "Gray" or "Ropes" does not match the competitor "Ropes & Gray".
 * 3. `resolveMention` assigns one mention to at most one entity: strong
 *    matches beat weak ones; within a class the longest matching name wins;
 *    our brand wins ties. So "Kirkland & Ellis" goes to Kirkland & Ellis
 *    rather than to a brand (ours or a competitor's) called "Kirkland", and
 *    "Bain Capital" goes to "Bain Capital" rather than "Bain Capital
 *    Ventures".
 *
 * The standalone predicates (`mentionMatchesAnyTerm`, and `isOurBrand` /
 * `isCompetitorBrand` built on it) only see ONE side. They answer "does this
 * mention name X?", not "who does this mention belong to?": with our brand
 * "Kirkland", `isOurBrand("Kirkland & Ellis")` is true even though
 * `resolveMention` gives that mention to the competitor. Any caller that
 * attributes a mention to exactly one side and has both sides in hand must use
 * `resolveMention`.
 *
 * Pure — no db, no server-only imports. Exported from `@repo/core/client`.
 */

/**
 * Trailing company-form tokens dropped from the end of a name. Kept short and
 * unambiguous on purpose: each one only drops as a FINAL token, and never when
 * it is the whole name.
 */
const LEGAL_SUFFIXES = new Set([
	"llp",
	"lllp",
	"llc",
	"pllc",
	"lp",
	"inc",
	"incorporated",
	"ltd",
	"limited",
	"corp",
	"corporation",
	"co",
	"pc",
	"plc",
	"gmbh",
	"ag",
	"sa",
	"nv",
	"bv",
	"pty",
	"srl",
	// "P.A." (professional association) — the initials join below turns
	// "P. A ." into "pa" first.
	"pa",
]);

const NORMALIZE_CACHE_LIMIT = 5000;
const normalizeCache = new Map<string, string>();
const prefixCache = new Map<string, string>();

function normalizeUncached(value: string): string {
	let text = value
		.normalize("NFKD")
		.replace(/\p{M}+/gu, "")
		.toLowerCase();

	// Markdown links keep their label: "[Ropes & Gray](https://...)".
	text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1");
	// Emoji and pictographic symbols ("🥇", "🏅", "✅"), plus the joiners and
	// variation selectors that ride along with them. Removed before the list
	// markers so "🥈 2. Foo" and "1. 🥇 Foo" both lose their prefix.
	text = text.replace(
		/\p{Extended_Pictographic}|\p{So}|\u200d|\ufe0f/gu,
		" ",
	);
	// Emphasis / code markers: "**Ropes & Gray**", "`Acme`".
	text = text.replace(/[*`~]+/g, "").replace(/__+/g, "");
	// Leading list / heading markers: "1. ", "2) ", "- ", "+ ", "• ", "## ",
	// repeated ("- 1. Foo").
	let previous: string;
	do {
		previous = text;
		text = text.replace(/^\s*(?:#+\s*|[-+•]\s+|\d+\s*[.):-]\s+)/u, "");
	} while (text !== previous);

	// "&" and "+" as a joiner read as "and"; a trailing "+" (Disney+) reads as
	// "plus" so it does not turn into a dangling "and".
	text = text
		.replace(/\s*&\s*/g, " and ")
		.replace(/(\p{L}|\p{N})\+(?=\p{L}|\p{N})/gu, "$1 and ")
		.replace(/\s\+\s/g, " and ")
		.replace(/\+/g, " plus ");

	// Apostrophes join their neighbours: "McDonald's" == "McDonalds",
	// "O'Melveny" → "omelveny". Mentions are extracted brand names, not prose,
	// so an apostrophe-s is part of the name rather than a possessive.
	text = text.replace(/['’‘`]/g, "");

	// Everything else that is not a letter, digit or space separates words.
	text = text
		.replace(/[^\p{L}\p{N}\s]/gu, " ")
		.replace(/\s+/g, " ")
		.trim();

	if (!text) return "";

	// "and" is a connector, not part of a brand's identity: "Ropes & Gray",
	// "Ropes and Gray" and "Ropes Gray" are the same firm. Drop it unless it is
	// the whole name.
	let tokens = text.split(" ");
	const withoutAnd = tokens.filter((t) => t !== "and");
	if (withoutAnd.length > 0) tokens = withoutAnd;

	// Runs of single-letter tokens are initials: "P. A ." → "p a" → "pa",
	// "U.S.A." → "u s a" → "usa". Joined before the suffix check so "pa" can
	// drop as a legal suffix.
	const joined: string[] = [];
	let lastWasInitials = false;
	for (const token of tokens) {
		const isInitial = /^\p{L}$/u.test(token);
		if (isInitial && lastWasInitials) {
			joined[joined.length - 1] += token;
		} else {
			joined.push(token);
		}
		lastWasInitials = isInitial;
	}
	tokens = joined;

	// Trailing legal suffixes: "Goldman Sachs & Co. LLC" → "goldman sachs".
	while (tokens.length > 1 && LEGAL_SUFFIXES.has(tokens[tokens.length - 1])) {
		tokens.pop();
	}
	return tokens.join(" ");
}

/**
 * Normalize a brand name, alias or raw mention string for matching. See the
 * module doc for the rules. Returns "" for empty / punctuation-only input.
 */
export function normalizeBrandMentionText(
	value: string | null | undefined,
): string {
	if (!value) return "";
	const cached = normalizeCache.get(value);
	if (cached !== undefined) return cached;
	const normalized = normalizeUncached(value);
	if (normalizeCache.size >= NORMALIZE_CACHE_LIMIT) normalizeCache.clear();
	normalizeCache.set(value, normalized);
	return normalized;
}

/**
 * `normalizeBrandMentionText` without the memo cache, for one-off inputs such
 * as the lines of a fetched page (`brand-mention-scan`). Page lines are never
 * seen twice, so caching them would only evict the brand names the cache is
 * for. Same rules, same output.
 */
export function normalizeBrandMentionTextUncached(
	value: string | null | undefined,
): string {
	return value ? normalizeUncached(value) : "";
}

/** Normalize, drop empties and dedupe a list of names/aliases. */
export function buildBrandMatchTerms(
	values: ReadonlyArray<string | null | undefined>,
): string[] {
	const terms = new Set<string>();
	for (const value of values) {
		if (typeof value !== "string") continue;
		const term = normalizeBrandMentionText(value);
		if (term) terms.add(term);
	}
	return Array.from(terms);
}

/**
 * Whether an already-normalized term matches an already-normalized mention:
 * equal, or present inside it as a whole-word run.
 */
export function normalizedTermMatches(
	normalizedMention: string,
	normalizedTerm: string,
): boolean {
	if (!normalizedMention || !normalizedTerm) return false;
	if (normalizedMention === normalizedTerm) return true;
	return ` ${normalizedMention} `.includes(` ${normalizedTerm} `);
}

/**
 * The mention form used for PREFIX matching: markdown link labels kept, then
 * anything from the first colon on and every parenthetical dropped, so
 * trailing notes do not block a prefix — "Bernstein Shur (Portland):[...]"
 * → "bernstein shur". Only used for the prefix rule; equality / containment
 * see the whole mention.
 */
function normalizePrefixMention(value: string): string {
	const cached = prefixCache.get(value);
	if (cached !== undefined) return cached;
	const trimmed = value
		.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
		.replace(/:[\s\S]*$/, "")
		.replace(/\([^)]*\)?/g, " ");
	const normalized = normalizeBrandMentionText(trimmed);
	if (prefixCache.size >= NORMALIZE_CACHE_LIMIT) prefixCache.clear();
	prefixCache.set(value, normalized);
	return normalized;
}

/** A mention prepared once for scoring against many terms. */
interface PreparedMention {
	/** Full normalized mention (equality / containment). */
	full: string;
	/** Tokens of the prefix form (see `normalizePrefixMention`). */
	prefixTokens: string[];
}

function prepareMention(
	mention: string | null | undefined,
): PreparedMention | null {
	const full = normalizeBrandMentionText(mention);
	if (!full || !mention) return null;
	const prefix = normalizePrefixMention(mention);
	return { full, prefixTokens: prefix ? prefix.split(" ") : [] };
}

/** Match classes, strongest first. */
const EXACT_OR_CONTAINED = 2;
const LEADING_PREFIX = 1;
const CLASS_WEIGHT = 1_000_000;

/**
 * Score one normalized term against a prepared mention; 0 = no match.
 *
 * - Class 2: the term equals the mention or appears in it as a whole-word run.
 * - Class 1: the mention (prefix form) is a LEADING run of the term's tokens,
 *   at least two tokens long and shorter than the term — "Kruze Consulting"
 *   for "Kruze Consulting - Startup Accounting, Finance, HR & Tax". A single
 *   token never prefix-matches ("Ropes" is not "Ropes Gray").
 *
 * Higher class always wins; within a class the longer term wins.
 */
function termMatchScore(mention: PreparedMention, term: string): number {
	if (!term) return 0;
	if (normalizedTermMatches(mention.full, term)) {
		return EXACT_OR_CONTAINED * CLASS_WEIGHT + term.length;
	}
	const leading = mention.prefixTokens;
	if (leading.length >= 2) {
		const termTokens = term.split(" ");
		if (
			termTokens.length > leading.length &&
			leading.every((token, i) => termTokens[i] === token)
		) {
			return LEADING_PREFIX * CLASS_WEIGHT + term.length;
		}
	}
	return 0;
}

/** Whether a raw name/alias matches a raw mention string. */
export function mentionMatchesTerm(
	mention: string | null | undefined,
	term: string | null | undefined,
): boolean {
	const prepared = prepareMention(mention);
	return (
		prepared !== null &&
		termMatchScore(prepared, normalizeBrandMentionText(term)) > 0
	);
}

/** Whether a raw mention matches any of the raw names/aliases. */
export function mentionMatchesAnyTerm(
	mention: string | null | undefined,
	terms: ReadonlyArray<string | null | undefined>,
): boolean {
	const prepared = prepareMention(mention);
	if (!prepared) return false;
	for (const term of terms) {
		if (termMatchScore(prepared, normalizeBrandMentionText(term)) > 0) {
			return true;
		}
	}
	return false;
}

/** The focal brand as the matcher needs it. */
export interface BrandMentionFocal {
	/** Primary name (usually the brand snapshot's `data.name`). */
	name?: string | null;
	/** Property aliases (companyName first, then stored aliases). */
	aliases?: ReadonlyArray<string | null | undefined> | null;
}

/** A tracked competitor as the matcher needs it. */
export interface BrandMentionCompetitor {
	id: string;
	name?: string | null;
	url?: string | null;
	aliases?: ReadonlyArray<string | null | undefined> | null;
}

/** Prebuilt, normalized terms for one focal brand + its competitors. */
export interface BrandMentionMatcher {
	focalTerms: string[];
	competitors: Array<{ id: string; terms: string[] }>;
}

export type ResolvedMention =
	| { kind: "self" }
	| { kind: "competitor"; id: string };

/** Domain part of a URL ("https://www.ropesgray.com/en" → "ropesgray.com"). */
function domainOf(url: string | null | undefined): string {
	if (!url) return "";
	return url
		.toLowerCase()
		.replace(/^https?:\/\//, "")
		.replace(/^www\./, "")
		.split("/")[0]
		.trim();
}

/**
 * Terms a competitor is matched on: its name (or, with no name, its domain —
 * the long-standing fallback) plus every alias.
 */
export function competitorMatchTerms(
	competitor: Omit<BrandMentionCompetitor, "id">,
): string[] {
	const primary = competitor.name || domainOf(competitor.url);
	return buildBrandMatchTerms([primary, ...(competitor.aliases ?? [])]);
}

/**
 * Build a matcher once and reuse it for every mention. Pass `focal: null` for
 * a subject with no focal brand (CATEGORY): nothing then resolves to "self".
 */
export function buildBrandMentionMatcher(input: {
	focal: BrandMentionFocal | null | undefined;
	competitors: ReadonlyArray<BrandMentionCompetitor> | null | undefined;
}): BrandMentionMatcher {
	const focalTerms = input.focal
		? buildBrandMatchTerms([
				input.focal.name,
				...(input.focal.aliases ?? []),
			])
		: [];
	const competitors = (input.competitors ?? [])
		.map((c) => ({ id: c.id, terms: competitorMatchTerms(c) }))
		.filter((c) => c.terms.length > 0);
	return { focalTerms, competitors };
}

/** Best match score over a term list, or 0 when none matches. */
function bestMatchScore(mention: PreparedMention, terms: string[]): number {
	let best = 0;
	for (const term of terms) {
		const score = termMatchScore(mention, term);
		if (score > best) best = score;
	}
	return best;
}

/**
 * Resolve one raw mention to our brand, exactly one competitor, or nothing.
 *
 * Matches are ranked: an exact / whole-word-containment match beats a
 * leading-prefix match regardless of length; within the same class the
 * longest matching term wins; our brand wins ties, and among competitors a
 * tie keeps the one listed first. So with our brand "Kirkland" and a
 * competitor "Kirkland & Ellis", "Kirkland & Ellis LLP" is the competitor and
 * a bare "Kirkland" is us; with competitors "Bain Capital" and "Bain Capital
 * Ventures", a "Bain Capital" mention goes to Bain Capital (exact beats
 * prefix). One mention is never counted for two entities.
 */
export function resolveMention(
	mention: string | null | undefined,
	matcher: BrandMentionMatcher,
): ResolvedMention | null {
	const prepared = prepareMention(mention);
	if (!prepared) return null;

	const selfScore = bestMatchScore(prepared, matcher.focalTerms);

	let bestId: string | null = null;
	let bestScore = 0;
	for (const competitor of matcher.competitors) {
		const score = bestMatchScore(prepared, competitor.terms);
		if (score > bestScore) {
			bestScore = score;
			bestId = competitor.id;
		}
	}

	if (selfScore > 0 && selfScore >= bestScore) return { kind: "self" };
	return bestId === null ? null : { kind: "competitor", id: bestId };
}
